import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useWhitepapers, type WhitepaperCardItem } from '@/hooks/useWhitepapers';
import { usePublications, type PublicationCardItem } from '@/hooks/usePublications';
import Loader from '@/components/Common/Loader';
import { sendGAPageview } from '../utils/gaConsent';
import SharedButton from '@/global/Button/Button';
import useText from '@/hooks/useText';

type ResourcesTab = 'whitepapers' | 'publications' | 'all';
type PublicationsSort = 'newest' | 'oldest';

const RESOURCES_TABS: ResourcesTab[] = ['publications', 'whitepapers', 'all'];
const DEFAULT_RESOURCES_TAB: ResourcesTab = 'all';

const parseResourcesTab = (value: string | null): ResourcesTab => {
  if (value && RESOURCES_TABS.includes(value as ResourcesTab)) {
    return value as ResourcesTab;
  }
  return DEFAULT_RESOURCES_TAB;
};

const parseWhitepaperId = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const buildPublicationsPath = (tab: ResourcesTab, whitepaperId?: number | null) => {
  if (whitepaperId) {
    return `/publications/whitepapers/${whitepaperId}`;
  }
  if (tab === DEFAULT_RESOURCES_TAB) {
    return '/publications';
  }
  return `/publications/${tab}`;
};

const readLegacyUrlParams = (search: string, hash: string) => {
  const fromSearch = new URLSearchParams(search);
  const fromHash = new URLSearchParams(hash.replace(/^#/, ''));
  return {
    tab: fromSearch.get('tab') || fromHash.get('tab'),
    whitepaper: fromSearch.get('whitepaper') || fromHash.get('whitepaper'),
  };
};

type CombinedResource =
  | { kind: 'publication'; date: string; year: string; publication: PublicationCardItem }
  | { kind: 'whitepaper'; date: string; year: string; whitepaper: WhitepaperCardItem };

const getResourceYear = (date: string) => {
  const trimmed = date.trim();
  if (/^\d{4}$/.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) return String(parsed.getFullYear());

  const match = trimmed.match(/\b(19|20)\d{2}\b/);
  return match?.[0] ?? '';
};

const parseResourceYear = (year: string) => {
  const value = Number(year);
  return Number.isFinite(value) ? value : 0;
};

const sortResourcesByDate = (items: CombinedResource[], sort: PublicationsSort) =>
  [...items].sort((a, b) =>
    sort === 'newest'
      ? parseResourceYear(b.year) - parseResourceYear(a.year)
      : parseResourceYear(a.year) - parseResourceYear(b.year),
  );

const tabButtonClass = (isActive: boolean) =>
  [
    'min-w-[9rem] rounded-lg px-5 py-2.5 text-sm transition-all duration-200',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue',
    isActive
      ? 'bg-white font-semibold text-blue shadow-sm ring-1 ring-slate-200/90 dark:bg-slate-900 dark:text-slate-50 dark:ring-pink-400/35 dark:shadow-[0_0_0_1px_rgba(244,114,182,0.15)]'
      : 'font-medium text-slate-600 hover:bg-white/70 hover:text-blue dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-100',
  ].join(' ');

const Whitepapers = () => {
  const publicationsTitle = useText('publications.title');
  const publicationsSubtitle = useText('publications.subtitle');
  const location = useLocation();
  const navigate = useNavigate();
  const { tabId } = useParams<{
    tabId?: string;
  }>();

  const resourcesTab = parseResourcesTab(tabId ?? null);

  const isAllTab = resourcesTab === 'all';
  const isPublicationsTab = resourcesTab === 'publications';
  const isWhitepapersTab = resourcesTab === 'whitepapers';
  const { whitepapers, loading: whitepapersLoading, error: whitepapersError } = useWhitepapers();
  const {
    publications,
    loading: publicationsLoading,
    error: publicationsError,
  } = usePublications(isPublicationsTab || isAllTab);
  const [selectedPublicationYear, setSelectedPublicationYear] = useState<string>('all');
  const [publicationsSort, setPublicationsSort] = useState<PublicationsSort>('newest');
  const publicationYears = useMemo(
    () =>
      Array.from(new Set(publications.map(publication => publication.date)))
        .filter(Boolean)
        .sort((a, b) => Number(b) - Number(a)),
    [publications],
  );

  const allResources = useMemo<CombinedResource[]>(
    () => [
      ...publications.map(publication => ({
        kind: 'publication' as const,
        date: publication.date,
        year: getResourceYear(publication.date),
        publication,
      })),
      ...whitepapers.map(whitepaper => ({
        kind: 'whitepaper' as const,
        date: whitepaper.date,
        year: getResourceYear(whitepaper.date),
        whitepaper,
      })),
    ],
    [publications, whitepapers],
  );

  const allResourceYears = useMemo(
    () =>
      Array.from(new Set(allResources.map(resource => resource.year)))
        .filter(Boolean)
        .sort((a, b) => Number(b) - Number(a)),
    [allResources],
  );

  const filteredPublications = useMemo(() => {
    const byYear =
      selectedPublicationYear === 'all'
        ? publications
        : publications.filter(publication => publication.date === selectedPublicationYear);

    return [...byYear].sort((a, b) =>
      publicationsSort === 'newest' ? Number(b.date) - Number(a.date) : Number(a.date) - Number(b.date),
    );
  }, [publications, selectedPublicationYear, publicationsSort]);

  const filteredAllResources = useMemo(() => {
    const byYear =
      selectedPublicationYear === 'all'
        ? allResources
        : allResources.filter(resource => resource.year === selectedPublicationYear);

    return sortResourcesByDate(byYear, publicationsSort);
  }, [allResources, selectedPublicationYear, publicationsSort]);

  const allResourcesLoading = publicationsLoading || whitepapersLoading;
  const filterYears = isAllTab ? allResourceYears : publicationYears;

  const navigateToPublications = useCallback(
    (tab: ResourcesTab, whitepaperId?: number | null) => {
      navigate(buildPublicationsPath(tab, whitepaperId), { replace: true });
    },
    [navigate],
  );

  const setResourcesTab = useCallback(
    (tab: ResourcesTab) => {
      navigateToPublications(tab);
    },
    [navigateToPublications],
  );

  useEffect(() => {
    const legacy = readLegacyUrlParams(location.search, location.hash);
    if (!legacy.tab && !legacy.whitepaper) return;

    const target = buildPublicationsPath(parseResourcesTab(legacy.tab), parseWhitepaperId(legacy.whitepaper));
    navigate(target, { replace: true });
  }, [location.search, location.hash, navigate]);

  useEffect(() => {
    if (tabId && !RESOURCES_TABS.includes(tabId as ResourcesTab)) {
      navigate('/publications', { replace: true });
    }
  }, [tabId, navigate]);

  useEffect(() => {
    sendGAPageview({ hitType: 'pageview', page: location.pathname, title: 'Publications' });
  }, [location.pathname]);

  const handleOpenForm = (id: number) => {
    navigateToPublications('whitepapers', id);
  };

  const resourceFiltersBar = (itemCount: number, countLabel: string) => (
    <div className="mb-6 mt-4 flex flex-col gap-3 rounded-xl border border-slate-200/80 bg-white p-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 ">
          Year
          <select
            className="mt-1 block min-w-[8rem] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue focus:outline-none dark:border-white/15 dark:bg-slate-900 dark:text-slate-200"
            value={selectedPublicationYear}
            onChange={event => setSelectedPublicationYear(event.target.value)}
          >
            <option value="all">All years</option>
            {filterYears.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
          Order
          <select
            className="mt-1 block min-w-[10rem] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue focus:outline-none dark:border-white/15 dark:bg-slate-900 dark:text-slate-200"
            value={publicationsSort}
            onChange={event => setPublicationsSort(event.target.value as PublicationsSort)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
      </div>

      <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
        {itemCount} {countLabel}
        {itemCount === 1 ? '' : 's'}
      </p>
    </div>
  );

  const renderPublicationCard = (pub: PublicationCardItem) => (
    <article
      key={`publication-${pub.id}`}
      className="landing-card-dark brand-glow-card brand-hover-card flex h-full flex-col border border-slate-200/70 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900/55"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">{pub.date}</p>
      <h2 className="mt-3 text-xl font-bold tracking-tight text-blue sm:text-2xl">{pub.title}</h2>
      {pub.journal ? (
        <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">{pub.journal}</p>
      ) : null}
      {pub.doi ? (
        <p className="mt-4 font-mono text-xs text-slate-600 dark:text-slate-400">DOI: {pub.doi}</p>
      ) : null}

      <div className="mt-auto flex flex-wrap gap-3 pt-8">
        {pub.doiUrl ? (
          <SharedButton
            type="button"
            variant="primary"
            size="sm"
            onClick={() => window.open(pub.doiUrl, '_blank')}
            className="ml-0"
          >
            View on DOI
          </SharedButton>
        ) : null}
        {pub.externalLink ? (
          <a
            href={pub.externalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-blue shadow-sm transition hover:bg-slate-50 dark:border-white/20 dark:bg-slate-900 dark:hover:bg-slate-800"
          >
            Open link
          </a>
        ) : null}
      </div>
    </article>
  );

  const renderWhitepaperCard = (whitepaper: WhitepaperCardItem, showKindBadge = false) => (
    <article
      key={`whitepaper-${whitepaper.id}`}
      className="landing-card-dark brand-glow-card brand-hover-card flex h-full flex-col border border-slate-200/70 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900/55"
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">{whitepaper.date}</p>
        {showKindBadge ? (
          <span className="rounded-full bg-pink/10 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-pink mb-3">
            Whitepaper
          </span>
        ) : null}
      </div>
      <h2 className="mt-3 text-2xl font-bold tracking-tight text-blue">{whitepaper.title}</h2>
      <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
        {whitepaper.description || 'Read this whitepaper to see all details.'}
      </p>

      <div className="mt-auto pt-8">
        <SharedButton
          type="button"
          variant="primary"
          size="sm"
          onClick={() => handleOpenForm(whitepaper.id)}
          className="ml-0"
        >
          Read whitepaper
        </SharedButton>
      </div>
    </article>
  );

  const renderCombinedResourceCard = (resource: CombinedResource) => {
    if (resource.kind === 'publication') {
      const pub = resource.publication;
      return (
        <article
          key={`all-publication-${pub.id}`}
          className="landing-card-dark brand-glow-card brand-hover-card flex h-full flex-col border border-slate-200/70 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900/55"
        >
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">{pub.date}</p>
            <span className="rounded-full bg-blue/10 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-blue mb-3">
              Publication
            </span>
          </div>
          <h2 className="mt-3 text-xl font-bold tracking-tight text-blue sm:text-2xl">{pub.title}</h2>
          {pub.journal ? (
            <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">{pub.journal}</p>
          ) : null}
          {pub.doi ? (
            <p className="mt-4 font-mono text-xs text-slate-600 dark:text-slate-400">DOI: {pub.doi}</p>
          ) : null}

          <div className="mt-auto flex flex-wrap gap-3 pt-8">
            {pub.doiUrl ? (
              <SharedButton
                type="button"
                variant="primary"
                size="sm"
                onClick={() => window.open(pub.doiUrl, '_blank')}
                className="ml-0"
              >
                View on DOI
              </SharedButton>
            ) : null}
            {pub.externalLink ? (
              <a
                href={pub.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-blue shadow-sm transition hover:bg-slate-50 dark:border-white/20 dark:bg-slate-900 dark:hover:bg-slate-800"
              >
                Open link
              </a>
            ) : null}
          </div>
        </article>
      );
    }

    return renderWhitepaperCard(resource.whitepaper, true);
  };

  return (
    <>
      <Helmet>
        <title>DISGENET: Publications</title>
        <meta
          name="description"
          content="Explore DISGENET whitepapers and scientific publications from MedBioinformatics."
        />
        <meta property="og:title" content="DISGENET: Publications" />
        <meta
          property="og:description"
          content="Explore DISGENET whitepapers and scientific publications from MedBioinformatics."
        />
        <meta property="og:url" content="https://www.disgenet.com/publications" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/publications" />
      </Helmet>

      <section className="landing-surface-b border-b border-slate-200/80 py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-secondary">Resources</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-blue sm:text-5xl lg:text-[3.15rem] lg:leading-tight">
              {publicationsTitle}
            </h1>
            <p className="max-w-3xl text-base leading-relaxed text-slate-600 dark:text-slate-400 sm:text-lg">
              {publicationsSubtitle}
            </p>
          </div>
        </div>
      </section>

      <section className="landing-section landing-surface-a pt-5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div
            className="mb-8 inline-flex max-w-full flex-wrap gap-1 rounded-xl border border-slate-200/80 bg-slate-100/90 p-1 shadow-inner dark:border-white/10 dark:bg-slate-800/55"
            role="tablist"
            aria-label="Resource library sections"
          >
            <button
              type="button"
              role="tab"
              aria-selected={resourcesTab === 'publications'}
              className={tabButtonClass(resourcesTab === 'publications')}
              onClick={() => setResourcesTab('publications')}
            >
              Publications
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={resourcesTab === 'whitepapers'}
              className={tabButtonClass(resourcesTab === 'whitepapers')}
              onClick={() => setResourcesTab('whitepapers')}
            >
              Whitepapers
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={resourcesTab === 'all'}
              className={tabButtonClass(resourcesTab === 'all')}
              onClick={() => setResourcesTab('all')}
            >
              All
            </button>
          </div>

          {isAllTab ? (
            <>
              {allResourcesLoading ? (
                <div className="py-12">
                  <Loader text="Loading resources..." isInnerSpinner />
                </div>
              ) : null}

              {!allResourcesLoading && publicationsError ? (
                <div className="landing-card-dark mb-4 border border-red-200/70 bg-red-50/70 p-6 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-950/30 dark:text-red-300">
                  Publications: {publicationsError}
                </div>
              ) : null}

              {!allResourcesLoading && whitepapersError ? (
                <div className="landing-card-dark mb-4 border border-red-200/70 bg-red-50/70 p-6 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-950/30 dark:text-red-300">
                  Whitepapers: {whitepapersError}
                </div>
              ) : null}

              {!allResourcesLoading && !publicationsError && !whitepapersError ? (
                allResources.length === 0 ? (
                  <p className="py-8 text-sm text-slate-600 dark:text-slate-400">
                    No publications or whitepapers are available at the moment.
                  </p>
                ) : (
                  <>
                    {resourceFiltersBar(filteredAllResources.length, 'resource')}

                    {filteredAllResources.length === 0 ? (
                      <p className="py-8 text-sm text-slate-600 dark:text-slate-400">
                        No resources found for the selected year.
                      </p>
                    ) : (
                      <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredAllResources.map(resource => renderCombinedResourceCard(resource))}
                      </div>
                    )}
                  </>
                )
              ) : null}
            </>
          ) : null}

          {isPublicationsTab ? (
            <>
              {publicationsLoading ? (
                <div className="py-12">
                  <Loader text="Loading publications..." isInnerSpinner />
                </div>
              ) : null}

              {!publicationsLoading && publicationsError ? (
                <div className="landing-card-dark border border-red-200/70 bg-red-50/70 p-6 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-950/30 dark:text-red-300">
                  {publicationsError}
                </div>
              ) : null}

              {!publicationsLoading && !publicationsError ? (
                publications.length === 0 ? (
                  <p className="py-8 text-sm text-slate-600 dark:text-slate-400">
                    No publications with the DISGENET tag are available at the moment.
                  </p>
                ) : (
                  <>
                    {resourceFiltersBar(filteredPublications.length, 'publication')}

                    {filteredPublications.length === 0 ? (
                      <p className="py-8 text-sm text-slate-600 dark:text-slate-400">
                        No publications found for the selected year.
                      </p>
                    ) : (
                      <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredPublications.map(pub => renderPublicationCard(pub))}
                      </div>
                    )}
                  </>
                )
              ) : null}
            </>
          ) : null}

          {isWhitepapersTab ? (
            <>
              {whitepapersLoading ? (
                <div className="py-12">
                  <Loader text="Loading whitepapers..." isInnerSpinner />
                </div>
              ) : null}

              {!whitepapersLoading && whitepapersError ? (
                <div className="landing-card-dark border border-red-200/70 bg-red-50/70 p-6 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-950/30 dark:text-red-300">
                  {whitepapersError}
                </div>
              ) : null}

              {!whitepapersLoading && !whitepapersError ? (
                whitepapers.length === 0 ? (
                  <p className="py-8 text-sm text-slate-600 dark:text-slate-400">
                    No whitepapers are available at the moment.
                  </p>
                ) : (
                  <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {whitepapers.map(whitepaper => renderWhitepaperCard(whitepaper))}
                  </div>
                )
              ) : null}
            </>
          ) : null}
        </div>
      </section>
    </>
  );
};

export default Whitepapers;
